import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Bookmark, BookmarkCheck, Minus, Plus, AlertCircle, ChevronUp, BookOpen, ScrollText, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import BookCover from "../components/BookCover";
import SiddurBookReader from "../components/SiddurBookReader";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ensureGematriaGershayim, digitsToHebrewGematria } from "../lib/hebrewGematria";

/**
 * Siddur — Ashkenaz Weekday Shacharit.
 *
 * Treated as a first-class book in the library: opens with the same
 * 3D closed-book cover the Tanakh chapters use, and supports the
 * same two reading modes (`book` vs `study`).
 *
 * Mode semantics
 * --------------
 *   • book  — clean prayer text only. Halacha instructions and
 *             interpretive `<small>` notes from Sefaria are hidden
 *             so the user can pray without visual clutter.
 *   • study — every `<small>` instruction / halacha note is shown
 *             above its paragraph in muted italics. Same content,
 *             more information.
 *
 * Text source
 * -----------
 * `/api/siddur/ashkenaz/shacharit` (cached server-side for 24h,
 * sourced exclusively from Sefaria — no invented content). The
 * server emits `paragraphs[]` strings that can contain `<b>` (opening
 * words) and `<small>` (instructions); the renderer below strips
 * everything else for safety.
 *
 * Conditional content
 * -------------------
 * `/api/siddur/tachanun-status` returns `is_mon_or_thu` and
 * `skip_tachanun` for today. The component hides the Torah Reading
 * section on non-Mon/Thu, hides the "For Monday and Thursday"
 * Tachanun subleaf same way, and shows a "no tachanun today" banner
 * with the HebCal reason (Rosh Chodesh / Yom Tov / etc.) when
 * applicable. The banner dims (60% opacity) the tachanun subsections
 * but doesn't collapse them — users can still expand to read.
 */
const FONT_SIZES = {
  s: { body: "text-[17px] sm:text-[18px]", lh: "leading-[1.85]" },
  m: { body: "text-[19px] sm:text-[21px]", lh: "leading-[2.0]" },
  l: { body: "text-[22px] sm:text-[25px]", lh: "leading-[2.1]" },
};
const FONT_SIZE_KEYS = ["s", "m", "l"];
const BOOKMARK_KEY = "tanakh_siddur_bookmark";
const FONT_KEY = "tanakh_siddur_font";
const MODE_KEY = "tanakh_siddur_mode";       // 'book' | 'study'
const OPENED_KEY = "tanakh_siddur_opened";   // sessionStorage skip-cover marker

// Vertical offset for scroll-to-section, accounting for the two
// stacked sticky bars at the top of the page:
//   • Site navbar (h-16) = 64px
//   • Pill section nav (py-2 + content) ≈ 56px
//   • + ~16px breathing room so the section title doesn't kiss the
//     bottom of the pill nav after a jump.
const HEADER_OFFSET_PX = 136;

export default function Siddur() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const dir = isEn ? "ltr" : "rtl";
  const [siddur, setSiddur] = useState(null);
  const [tStatus, setTStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [fontSize, setFontSize] = useState(() => {
    try { return localStorage.getItem(FONT_KEY) || "m"; } catch { return "m"; }
  });
  const [readingMode, setReadingMode] = useState(() => {
    try { return localStorage.getItem(MODE_KEY) === "study" ? "study" : "book"; }
    catch { return "book"; }
  });
  // Book-cover intro state — mirrors Reader.jsx's `bookOpen` /
  // `opening`. The cover is shown on first visit per session; once
  // the user opens it (or returns later in the same session) we
  // skip straight to the prayer text. Persisted in sessionStorage
  // (not localStorage) so the cover comes back on next-day visits,
  // matching the Tanakh chapter flow.
  const [bookOpen, setBookOpen] = useState(() => {
    try { return sessionStorage.getItem(OPENED_KEY) === "1"; } catch { return false; }
  });
  const [opening, setOpening] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [bookmark, setBookmark] = useState(() => {
    try { return localStorage.getItem(BOOKMARK_KEY); } catch { return null; }
  });
  // Imperative handle into the 3D book reader (book-mode only). The
  // section-nav chips and the prev/next page buttons drive it.
  const bookReaderRef = useRef(null);
  // Library page index (rendered order). Used to label the page-nav
  // bar "עמוד X מתוך Y" so the user knows where they are.
  const [bookPageIndex, setBookPageIndex] = useState(0);
  const [bookPageCount, setBookPageCount] = useState(0);
  // Track page count whenever the reader rebuilds (mode flip / data
  // refresh / first-time opening from the cover). Polls once shortly
  // after mount of book mode — cheap and keeps the folio counter
  // accurate without a forwarded subscription. `bookOpen` is in the
  // dep array so the count is captured the moment the cover gets
  // dismissed and the reader actually mounts (otherwise the folio
  // sits on "—" until the user makes their first flip).
  useEffect(() => {
    if (!siddur || readingMode !== "book" || !bookOpen) return;
    // Defer to next frame so the reader has time to mount.
    const id = setTimeout(() => {
      const c = bookReaderRef.current?.pageCount?.() ?? 0;
      setBookPageCount(c);
      // The library mounts at the LAST library index (because we
      // feed pages reversed), which is the FIRST semantic page.
      // Set bookPageIndex accordingly so the folio reads "1 / N"
      // on first render — without this, before the user's first
      // flip the folio shows "N / N".
      if (c > 0) setBookPageIndex(c - 1);
    }, 250);
    return () => clearTimeout(id);
  }, [siddur, readingMode, bookOpen]);
  // Pretty folio label. The library's `bookPageIndex` is in REVERSED
  // order (LTR-emulated RTL), so we translate to natural reading
  // order before display. Page 1 = first prayer.
  const bookPageDisplay = useMemo(() => {
    if (bookPageCount <= 0) return "—";
    const semantic = Math.max(0, bookPageCount - 1 - bookPageIndex);
    return `${semantic + 1} / ${bookPageCount}`;
  }, [bookPageIndex, bookPageCount]);
  const sectionRefs = useRef({});
  const restoredRef = useRef(false);

  // Persist font size when changed.
  useEffect(() => {
    try { localStorage.setItem(FONT_KEY, fontSize); } catch {}
  }, [fontSize]);

  // Persist reading mode (book vs study) so a returning user keeps
  // their preference. Stored in localStorage rather than session
  // because mode is a long-term preference, not a per-visit one.
  useEffect(() => {
    try { localStorage.setItem(MODE_KEY, readingMode); } catch {}
  }, [readingMode]);

  // Open the book with the same 850ms animation the Tanakh reader
  // uses, then flip to the prayer view.
  const handleOpenBook = useCallback(() => {
    setOpening(true);
    setTimeout(() => {
      try { sessionStorage.setItem(OPENED_KEY, "1"); } catch {}
      setBookOpen(true);
      setOpening(false);
    }, 850);
  }, []);

  const toggleMode = useCallback(() => {
    setReadingMode((m) => (m === "book" ? "study" : "book"));
  }, []);

  // Initial fetch — siddur text + today's tachanun status in parallel.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [s, ts] = await Promise.all([
          api.siddurShacharitAshkenaz(),
          api.siddurTachanunStatus().catch(() => null),
        ]);
        if (!alive) return;
        setSiddur(s);
        setTStatus(ts);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.detail || t("siddur_page.load_failed"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // After mount + render, restore the bookmark position once.
  useEffect(() => {
    if (restoredRef.current || !siddur || !bookmark) return;
    const el = sectionRefs.current[bookmark];
    if (el) {
      // Defer to next frame so layout has settled, then jump
      // instantly (no animation on initial restore — the user
      // didn't trigger this so the smooth scroll is jarring).
      requestAnimationFrame(() => {
        const targetY =
          el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET_PX;
        window.scrollTo({ top: Math.max(0, targetY), behavior: "auto" });
      });
      restoredRef.current = true;
    }
  }, [siddur, bookmark]);

  // IntersectionObserver — only used in study mode (book mode tracks
  // the current section via the flip-book's onPageFlip callback below).
  // Re-runs when mode changes so we clean up the observer when leaving
  // study mode.
  useEffect(() => {
    if (!siddur || readingMode !== "study") return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.getAttribute("data-section-id");
          if (id) setActiveSectionId(id);
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 },
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [siddur, readingMode]);

  // Filter sections by tags + tachanun status. The server already
  // tags Torah Reading + Tachanun subsections; here we just apply
  // weekday + holiday logic to decide visibility/banner.
  const visibleSections = useMemo(() => {
    if (!siddur) return [];
    const isMonThu = tStatus?.is_mon_or_thu;
    const skipTachanun = tStatus?.skip_tachanun;
    return siddur.sections
      .map((s) => ({ ...s }))
      .filter((s) => {
        // Torah Reading: only on Mon/Thu (when status is known).
        if (s.tags?.includes("monday-thursday") && tStatus && !isMonThu) {
          return false;
        }
        return true;
      })
      .map((s) => {
        // Filter individual subsections too — e.g. the "For Monday
        // and Thursday" leaf within Tachanun.
        const subsections = (s.subsections || []).filter((ss) => {
          if (ss.tags?.includes("monday-thursday") && tStatus && !isMonThu) {
            return false;
          }
          return true;
        });
        // Decide if any subsection is tagged 'tachanun' — drives the
        // skip banner.
        const hasTachanun = subsections.some((ss) => ss.tags?.includes("tachanun"));
        return { ...s, subsections, _hasTachanun: hasTachanun, _skipTachanun: hasTachanun && skipTachanun };
      });
  }, [siddur, tStatus]);

  // Pre-compute the target Y and use window.scrollTo for a deterministic
  // smooth scroll. The previous implementation called scrollIntoView
  // ({behavior:"smooth"}) and then window.scrollBy(-76) synchronously,
  // which interrupted the in-flight smooth animation and resulted in
  // either an over/undershoot or, on iOS Safari, a runaway scroll
  // (the "infinite scroll" symptom the user reported).
  const jumpTo = useCallback((sectionId) => {
    const el = sectionRefs.current[sectionId];
    if (!el) return;
    const targetY =
      el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET_PX;
    window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
  }, []);

  const saveBookmark = useCallback(() => {
    if (!activeSectionId) {
      toast.error(t("siddur_page.toast_no_position"));
      return;
    }
    try {
      localStorage.setItem(BOOKMARK_KEY, activeSectionId);
      setBookmark(activeSectionId);
      toast.success(t("siddur_page.toast_position_saved"));
    } catch {
      toast.error(t("siddur_page.toast_save_failed"));
    }
  }, [activeSectionId, t]);

  const clearBookmark = useCallback(() => {
    try { localStorage.removeItem(BOOKMARK_KEY); } catch {}
    setBookmark(null);
    toast.success(t("siddur_page.toast_bookmark_cleared"));
  }, [t]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" dir={dir} data-testid="siddur-loading">
        <div className="flex flex-col items-center gap-3 text-foreground/70">
          <Loader2 className="h-7 w-7 animate-spin text-gold" strokeWidth={1.5} />
          <p className="font-heading">{t("siddur_page.loading")}</p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" dir={dir} data-testid="siddur-error">
        <AlertCircle className="h-10 w-10 mx-auto text-amber-500" strokeWidth={1.5} />
        <p className="mt-4 font-heading text-lg">{err}</p>
        <p className="mt-2 text-sm text-muted-foreground">{t("siddur_page.try_refresh")}</p>
      </div>
    );
  }

  // ----------- CLOSED BOOK INTRO -----------
  // Same flow as /reader/:bookSlug — show the 3D cover, play the
  // open animation on tap, then reveal the prayer body. Skipped if
  // sessionStorage marks the siddur as already opened this session
  // (so going back-and-forth via the navbar doesn't replay the
  // intro every time).
  if (!bookOpen) {
    return (
      <div
        dir={dir}
        data-testid="siddur-cover-view"
        className="min-h-[calc(100vh-4rem)] flex flex-col items-center px-4 sm:px-6 py-6 sm:py-10 sm:justify-center pb-[calc(96px+env(safe-area-inset-bottom))] sm:pb-10"
      >
        <p className="font-latin italic text-[10px] sm:text-xs tracking-[0.35em] uppercase text-gold mb-4 sm:mb-10">
          <span className="hidden sm:inline">{t("siddur_page.tap_to_open")}</span>
          <span className="inline sm:hidden">{t("siddur_page.tap_to_open")}</span>
        </p>
        {/* Same dual-cover trick the Reader uses: a smaller cover on
            phones (incl. landscape) and a larger one on desktop. */}
        <div
          className="phone-cover-only w-full flex items-center justify-center"
          data-testid="siddur-cover-mobile"
        >
          <BookCover
            opening={opening}
            onOpen={handleOpenBook}
            size="md"
            title={t("siddur_page.cover_title")}
            subtitle={t("siddur_page.cover_subtitle")}
          />
        </div>
        <div className="desktop-cover-only">
          <BookCover
            opening={opening}
            onOpen={handleOpenBook}
            size="xl"
            title={t("siddur_page.cover_title")}
            subtitle={t("siddur_page.cover_subtitle")}
          />
        </div>
        <p className="mt-6 sm:mt-16 font-heading text-xl sm:text-2xl text-foreground text-center">
          {t("siddur_page.cover_nusach")}
        </p>
        <p className="mt-2 sm:mt-3 text-xs sm:text-sm text-muted-foreground max-w-md text-center px-2">
          {t("siddur_page.cover_hint")}
        </p>

        {/* Desktop: inline action buttons. */}
        <div className="mt-8 hidden sm:flex gap-3">
          <Button
            onClick={handleOpenBook}
            data-testid="siddur-open-book-cta"
            className="sacred-btn-gold"
          >
            <BookOpen className="h-4 w-4" strokeWidth={1.5} />
            {t("siddur_page.open_book")}
          </Button>
          <Button
            variant="ghost"
            onClick={toggleMode}
            data-testid="siddur-switch-to-study-from-entry"
            className="sacred-btn-ghost"
          >
            <ScrollText className="h-4 w-4" strokeWidth={1.5} />
            {readingMode === "study" ? t("siddur_page.mode_book") : t("siddur_page.mode_study")}
          </Button>
        </div>

        {/* Mobile-only sticky action bar — always reachable without scrolling. */}
        <div
          data-testid="siddur-cover-sticky-actions"
          className="fixed bottom-0 inset-x-0 z-30 sm:hidden border-t border-gold/30 bg-background/95 backdrop-blur-md"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex items-center gap-2 px-4 py-3">
            <Button
              onClick={handleOpenBook}
              data-testid="siddur-open-book-cta-mobile"
              className="sacred-btn-gold flex-1 justify-center"
            >
              <BookOpen className="h-4 w-4" strokeWidth={1.5} />
              {t("siddur_page.open_book")}
            </Button>
            <Button
              variant="ghost"
              onClick={toggleMode}
              data-testid="siddur-switch-to-study-from-entry-mobile"
              className="sacred-btn-ghost flex-1 justify-center"
            >
              <ScrollText className="h-4 w-4" strokeWidth={1.5} />
              {readingMode === "study" ? t("siddur_page.mode_book") : t("siddur_page.mode_study")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const fs = FONT_SIZES[fontSize] || FONT_SIZES.m;

  return (
    <div dir={dir} className="min-h-screen pb-32" data-testid="siddur-page">
      {/* Hero */}
      <div className="border-b border-border/60 bg-card/30">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 pt-10 pb-6 text-center space-y-2">
          <p className="font-latin italic text-[11px] tracking-[0.3em] uppercase text-gold/80">
            {t("siddur_page.header_eyebrow")}
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl text-foreground">
            {t("siddur_page.header_title")}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground font-heading">
            {t("siddur_page.header_subtitle")}
          </p>
          <div className="ornamental-divider text-gold/60">✦</div>

          {/* Reading-mode toggle — mirrors the Tanakh reader's
              book/study split. In `book` mode we hide the Sefaria
              halacha instructions (`<small>`) for a clean prayer
              experience; `study` mode reveals them. */}
          <div className="pt-3 flex justify-center">
            <button
              type="button"
              onClick={toggleMode}
              data-testid="siddur-mode-toggle"
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-card/60 hover:bg-gold/10 px-3 py-1.5 text-xs font-heading text-foreground/80"
              aria-label={readingMode === "book" ? t("siddur_page.switch_to_study") : t("siddur_page.switch_to_book")}
            >
              {readingMode === "book" ? (
                <>
                  <ScrollText className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                  {t("siddur_page.switch_to_study")}
                </>
              ) : (
                <>
                  <BookOpen className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                  {t("siddur_page.switch_to_book")}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Sticky section nav — same chips, but in book mode they
            call into the SiddurBookReader's `goToSection` instead of
            scrolling. The pill style is identical so the UI feels
            consistent across modes. */}
        <div className="sticky top-16 z-30 border-y border-border/60 bg-background/95 backdrop-blur-md">
          <div className="mx-auto max-w-3xl px-2 sm:px-4 py-2 overflow-x-auto" data-testid="siddur-section-nav">
            <div className="flex gap-1.5 min-w-max">
              {visibleSections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    if (readingMode === "book") {
                      bookReaderRef.current?.goToSection?.(s.id);
                    } else {
                      jumpTo(s.id);
                    }
                  }}
                  data-testid={`siddur-nav-${s.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-heading whitespace-nowrap transition-all border ${
                    activeSectionId === s.id
                      ? "bg-gold/20 text-foreground border-gold/50 shadow-sm"
                      : "bg-card/40 text-foreground/70 border-border/60 hover:bg-card/70 hover:border-gold/30"
                  }`}
                >
                  {isEn ? (s.title_en || s.title_he) : s.title_he}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Body — two rendering paths driven by `readingMode`:
            • book  → paginated 3D flip book (same mechanism as Tanakh)
            • study → continuous scroll with halacha instructions
          Mode switch is preserved across sessions in localStorage. */}
      {readingMode === "book" ? (
        <div className="px-2 sm:px-4 py-6" data-testid="siddur-book-mode">
          <SiddurBookReader
            ref={bookReaderRef}
            sections={visibleSections}
            tStatus={tStatus}
            showInstructions={false}
            onPageFlip={(libIdx) => {
              setBookPageIndex(libIdx);
              // Translate library index → semantic page, then read
              // which section that page belongs to, so the section
              // pill nav highlights correctly.
              const count = bookReaderRef.current?.pageCount?.() ?? 0;
              if (count > 0) {
                const semantic = Math.max(0, count - 1 - libIdx);
                // The book reader exposes pages internally, so the
                // simplest cross-check is to pick the section whose
                // first page index is ≤ semantic and the next is >.
                // We don't expose that map here, but `visibleSections`
                // is small (≤8) and `goToSection` already knows the
                // mapping — read it lazily through getCurrentSection
                // when needed. For now keep activeSectionId in sync
                // by triggering a small probe.
                const sec = bookReaderRef.current?.currentSectionId?.();
                if (sec) setActiveSectionId(sec);
              }
            }}
          />
          {/* Page-nav bar — prev/next + page indicator. */}
          <div
            className="fixed bottom-4 inset-x-0 z-40 flex justify-center pointer-events-none px-3"
            data-testid="siddur-book-nav"
          >
            <div className="pointer-events-auto flex items-center gap-2 sm:gap-3 rounded-full border border-gold/40 bg-card/95 backdrop-blur-md shadow-lg px-3 py-2">
              {/* In RTL "next page" sits on the LEFT, "prev page" on
                  the RIGHT — matches a real Hebrew sefer. */}
              <button
                type="button"
                onClick={() => bookReaderRef.current?.flipBackward?.()}
                className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-foreground/10"
                data-testid="siddur-book-prev"
                aria-label={t("siddur_page.prev_page")}
              >
                <ChevronRight className="h-4 w-4" strokeWidth={2} />
              </button>
              <span
                dir="ltr"
                className="text-xs font-heading text-foreground/80 min-w-[60px] text-center"
                data-testid="siddur-book-folio"
              >
                {bookPageDisplay}
              </span>
              <button
                type="button"
                onClick={() => bookReaderRef.current?.flipForward?.()}
                className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-foreground/10"
                data-testid="siddur-book-next"
                aria-label={t("siddur_page.next_page")}
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={2} />
              </button>
              <div className="h-6 w-px bg-border/60 mx-1" />
              <button
                type="button"
                onClick={toggleMode}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-heading text-foreground/80 hover:bg-foreground/10"
                data-testid="siddur-book-to-study"
                title={t("siddur_page.switch_to_study")}
              >
                <ScrollText className="h-4 w-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t("siddur_page.mode_study")}</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 space-y-12" data-testid="siddur-study-mode">
        {visibleSections.map((section) => (
          <section
            key={section.id}
            ref={(el) => { sectionRefs.current[section.id] = el; }}
            data-section-id={section.id}
            data-testid={`siddur-section-${section.id}`}
            className="scroll-mt-32"
          >
            <SectionHeader title={isEn ? (section.title_en || section.title_he) : section.title_he} subtitle={isEn ? section.title_he : section.title_en} />

            {section.tags?.includes("monday-thursday") && (
              <Banner tone="info" testid={`banner-mon-thu-${section.id}`}>
                {t("siddur_page.banner_mon_thu")}
              </Banner>
            )}

            {section._skipTachanun && (
              <Banner tone="warning" testid={`banner-skip-tachanun-${section.id}`}>
                <strong className="font-heading">{t("siddur_page.banner_no_tachanun")}</strong>
                {tStatus?.reason_he && (
                  <> — {isEn ? (tStatus.reason_en || tStatus.reason_he) : tStatus.reason_he}</>
                )}
                <span className="block mt-1 text-[11px] opacity-80">
                  {t("siddur_page.banner_no_tachanun_note")}
                </span>
              </Banner>
            )}

            <div className="space-y-8 mt-4">
              {section.subsections.map((ss) => (
                <Subsection
                  key={ss.id}
                  data={ss}
                  fontClasses={fs}
                  dimmed={ss.tags?.includes("tachanun") && section._skipTachanun}
                  showInstructions={readingMode === "study"}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Footer toolbar — font size + bookmark */}
      <div
        className="fixed bottom-4 inset-x-0 z-40 flex justify-center pointer-events-none px-3"
        data-testid="siddur-toolbar"
      >
        <div className="pointer-events-auto flex items-center gap-2 sm:gap-3 rounded-full border border-gold/40 bg-card/95 backdrop-blur-md shadow-lg px-3 py-2">
          <button
            type="button"
            onClick={() => {
              const i = FONT_SIZE_KEYS.indexOf(fontSize);
              if (i > 0) setFontSize(FONT_SIZE_KEYS[i - 1]);
            }}
            disabled={FONT_SIZE_KEYS.indexOf(fontSize) === 0}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-foreground/10 disabled:opacity-40"
            data-testid="siddur-font-smaller"
            aria-label={t("siddur_page.font_smaller")}
          >
            <Minus className="h-4 w-4" strokeWidth={2} />
          </button>
          <span className="text-xs font-heading text-foreground/80 w-7 text-center" data-testid="siddur-font-label">
            {fontSize === "s" ? "S" : fontSize === "l" ? "L" : "M"}
          </span>
          <button
            type="button"
            onClick={() => {
              const i = FONT_SIZE_KEYS.indexOf(fontSize);
              if (i < FONT_SIZE_KEYS.length - 1) setFontSize(FONT_SIZE_KEYS[i + 1]);
            }}
            disabled={FONT_SIZE_KEYS.indexOf(fontSize) === FONT_SIZE_KEYS.length - 1}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-foreground/10 disabled:opacity-40"
            data-testid="siddur-font-bigger"
            aria-label={t("siddur_page.font_bigger")}
          >
            <Plus className="h-4 w-4" strokeWidth={2} />
          </button>

          <div className="h-6 w-px bg-border/60 mx-1" />

          {bookmark ? (
            <button
              type="button"
              onClick={clearBookmark}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-heading bg-gold/15 text-foreground border border-gold/40 hover:bg-gold/25"
              data-testid="siddur-bookmark-clear"
              title={t("siddur_page.bookmark_clear")}
            >
              <BookmarkCheck className="h-4 w-4 text-gold" strokeWidth={2} />
              <span className="hidden sm:inline">{t("siddur_page.bookmark_saved")}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={saveBookmark}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-heading text-foreground/80 hover:bg-foreground/10"
              data-testid="siddur-bookmark-save"
              title={t("siddur_page.bookmark_save_pos")}
            >
              <Bookmark className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">{t("siddur_page.bookmark_save_pos")}</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-foreground/10"
            data-testid="siddur-scroll-top"
            aria-label={t("siddur_page.scroll_top")}
          >
            <ChevronUp className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-3 text-center sm:text-right" data-testid="siddur-section-header">
      <h2 className="font-heading text-2xl sm:text-3xl text-foreground">
        {title}
      </h2>
      <p className="font-latin text-[10px] tracking-[0.25em] uppercase text-gold/70 mt-1">
        {subtitle}
      </p>
      <div className="mt-2 h-px bg-gradient-to-l from-transparent via-gold/40 to-transparent" />
    </div>
  );
}

function Banner({ tone = "info", children, testid }) {
  const toneCls =
    tone === "warning"
      ? "bg-amber-500/10 border-amber-500/30 text-amber-800 dark:text-amber-200"
      : "bg-blue-500/10 border-blue-500/30 text-foreground/80";
  return (
    <div
      className={`mt-3 text-xs sm:text-[13px] rounded-sm border-s-2 ps-3 pe-2 py-2 ${toneCls}`}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

function Subsection({ data, fontClasses, dimmed, showInstructions }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  // Sefaria sometimes returns plain gematria letters without gershayim
  // ("תהילים קמו") and English with Arabic digits ("Psalm 146"). For
  // sanctity and parity with the Tanakh Book Mode, normalise both so
  // the displayed numerals are proper Hebrew letters: "תהילים קמ״ו"
  // and "Psalm קמ״ו".
  const titleHe = ensureGematriaGershayim(data.title_he);
  const titleEn = digitsToHebrewGematria(data.title_en);
  // In English mode, the English title becomes the main heading and
  // the Hebrew title becomes the muted eyebrow above.
  const mainTitle = isEn ? (titleEn || titleHe) : titleHe;
  const eyebrowTitle = isEn ? titleHe : titleEn;
  return (
    <article
      data-testid={`siddur-subsection-${data.id}`}
      data-ref={data.ref || ""}
      className={`scroll-mt-32 ${dimmed ? "opacity-60" : ""}`}
    >
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="font-heading text-lg sm:text-xl text-foreground/90">
          {mainTitle}
        </h3>
        <span className="font-latin text-[10px] tracking-wider uppercase text-gold/60">
          {eyebrowTitle}
        </span>
      </header>
      <div className={`space-y-4 ${fontClasses.lh}`}>
        {data.paragraphs.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            {t("siddur_page.passage_unavailable")}
          </p>
        )}
        {data.paragraphs.map((p, idx) => (
          <Paragraph
            key={idx}
            html={p}
            fontClasses={fontClasses}
            showInstructions={showInstructions}
          />
        ))}
      </div>
    </article>
  );
}

/**
 * Render a Sefaria-sourced paragraph. We accept only `<b>` and
 * `<small>` tags — everything else is stripped. The text comes
 * server-side from a known-safe source but the whitelist keeps us
 * safe against any future change at Sefaria's end.
 *
 * Strategy: split the string into tokens — plain text, <b>…</b>, and
 * <small>…</small> — by sequentially matching the three patterns, and
 * render each as a span. <small> stretches of text (halacha
 * instructions, niggun cues) are rendered as their own block above
 * the prayer text.
 *
 * `showInstructions` toggles the <small> block. In `book` reading
 * mode we hide the instructions for a clean prayer experience; in
 * `study` mode we show them — same content as the Tanakh reader's
 * book-vs-study split.
 */
function Paragraph({ html, fontClasses, showInstructions }) {
  const tokens = useMemo(() => parseSefariaHtml(html), [html]);

  // Group consecutive non-small tokens into the main paragraph, and
  // split out <small> as its own block. This gives a nice typographic
  // separation (instructions on top in muted small style, prayer
  // text below in full size).
  const instructionTokens = tokens.filter((t) => t.kind === "small");
  const textTokens = tokens.filter((t) => t.kind !== "small");

  return (
    <div>
      {showInstructions && instructionTokens.length > 0 && (
        <p className="text-xs sm:text-[13px] text-muted-foreground/90 italic font-heading mb-2">
          {instructionTokens.map((t, i) => (
            <span key={i}>{t.text}</span>
          ))}
        </p>
      )}
      {textTokens.length > 0 && (
        <p
          className={`font-hebrew ${fontClasses.body} text-foreground tracking-normal`}
          style={{ fontFamily: "'Frank Ruhl Libre', 'Noto Serif Hebrew', 'David Libre', 'SBL Hebrew', serif" }}
        >
          {textTokens.map((t, i) =>
            t.kind === "bold" ? (
              <strong key={i} className="text-gold font-semibold">{t.text}</strong>
            ) : (
              <span key={i}>{t.text}</span>
            ),
          )}
        </p>
      )}
    </div>
  );
}

/**
 * Tiny whitelist-aware HTML splitter. Returns an array of tokens:
 *   { kind: 'text'|'bold'|'small', text: '…' }
 * Any tag other than <b>/<small> is dropped (its inner text is kept
 * as plain text).
 */
function parseSefariaHtml(input) {
  if (!input) return [];
  // Normalise whitespace inside tags so the regex matches reliably.
  const re = /<(b|small)>([\s\S]*?)<\/\1>/gi;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(input)) !== null) {
    if (m.index > last) {
      out.push({ kind: "text", text: stripUnknownTags(input.slice(last, m.index)) });
    }
    out.push({
      kind: m[1].toLowerCase() === "b" ? "bold" : "small",
      text: stripUnknownTags(m[2]),
    });
    last = m.index + m[0].length;
  }
  if (last < input.length) {
    out.push({ kind: "text", text: stripUnknownTags(input.slice(last)) });
  }
  // Drop empty tokens.
  return out.filter((t) => (t.text || "").trim().length > 0);
}

function stripUnknownTags(s) {
  // Remove any remaining HTML tags (keep their inner content).
  return s.replace(/<[^>]+>/g, "");
}
